'use client';

import { useCallback, useMemo, useState } from 'react';

export type VerificationChannel = 'phone' | 'email';

export type ContactVerificationState = {
  isOpen: boolean;
  channel: VerificationChannel;
  destination: string;
  code: string;
  isSending: boolean;
  isConfirming: boolean;
  error: string | null;
  resent: boolean;
};

const readError = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({}) as Record<string, never>);
  const payload = data as { error?: string; message?: string };
  return payload.error || payload.message || fallback;
};

export function useContactVerification(options: {
  onVerified: (verificationToken: string) => void | Promise<void>;
}) {
  const { onVerified } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [channel, setChannel] = useState<VerificationChannel>('phone');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  const [verifiedKey, setVerifiedKey] = useState<string | null>(null);

  const sendCode = useCallback(
    async (
      nextChannel: VerificationChannel,
      nextDestination: string,
      firstName?: string
    ): Promise<boolean> => {
      setIsSending(true);
      setError(null);
      try {
        const response = await fetch('/api/v1.0/public/verification/request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channel: nextChannel,
            destination: nextDestination,
            firstName: firstName?.trim() || undefined
          })
        });

        if (!response.ok) {
          setError(
            await readError(
              response,
              'We could not send a code. Please try again.'
            )
          );
          return false;
        }

        return true;
      } catch {
        setError('We could not send a code. Please check your connection.');
        return false;
      } finally {
        setIsSending(false);
      }
    },
    []
  );

  const start = useCallback(
    async (input: {
      channel: VerificationChannel;
      destination: string;
      firstName?: string;
    }): Promise<{ alreadyVerified: boolean; token: string | null }> => {
      const key = `${input.channel}:${input.destination.trim().toLowerCase()}`;
      if (verifiedToken && verifiedKey === key) {
        return { alreadyVerified: true, token: verifiedToken };
      }

      setChannel(input.channel);
      setDestination(input.destination);
      setCode('');
      setResent(false);
      setIsOpen(true);

      await sendCode(input.channel, input.destination, input.firstName);
      return { alreadyVerified: false, token: null };
    },
    [sendCode, verifiedToken, verifiedKey]
  );

  const confirm = useCallback(
    async (submittedCode?: string) => {
      const value = (submittedCode ?? code).trim();
      if (value.length < 4) return;

      setIsConfirming(true);
      setError(null);
      try {
        const response = await fetch('/api/v1.0/public/verification/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel, destination, code: value })
        });

        if (!response.ok) {
          setError(
            await readError(response, 'That code is incorrect. Try again.')
          );
          setCode('');
          return;
        }

        const payload = (await response.json().catch(() => ({}))) as {
          data?: { verificationToken?: string };
          verificationToken?: string;
        };
        const token =
          payload.data?.verificationToken ?? payload.verificationToken ?? null;

        if (!token) {
          setError('Verification failed. Please try again.');
          return;
        }

        setVerifiedToken(token);
        setVerifiedKey(`${channel}:${destination.trim().toLowerCase()}`);
        setIsOpen(false);
        await onVerified(token);
      } catch {
        setError('We could not check that code. Please try again.');
      } finally {
        setIsConfirming(false);
      }
    },
    [channel, code, destination, onVerified]
  );

  const handleCodeChange = useCallback(
    (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setCode(digits);
      if (digits.length === 6 && !isConfirming) {
        void confirm(digits);
      }
    },
    [confirm, isConfirming]
  );

  const resend = useCallback(async () => {
    setResent(false);
    const sent = await sendCode(channel, destination);
    if (sent) setResent(true);
  }, [channel, destination, sendCode]);

  const switchChannel = useCallback(
    async (nextChannel: VerificationChannel, nextDestination: string) => {
      setChannel(nextChannel);
      setDestination(nextDestination);
      setCode('');
      setResent(false);
      setIsOpen(true);
      await sendCode(nextChannel, nextDestination);
    },
    [sendCode]
  );

  const cancel = useCallback(() => {
    setIsOpen(false);
    setCode('');
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setVerifiedToken(null);
    setVerifiedKey(null);
  }, []);

  const state = useMemo<ContactVerificationState>(
    () => ({
      isOpen,
      channel,
      destination,
      code,
      isSending,
      isConfirming,
      error,
      resent
    }),
    [isOpen, channel, destination, code, isSending, isConfirming, error, resent]
  );

  return {
    ...state,
    verificationToken: verifiedToken,
    start,
    confirm,
    handleCodeChange,
    resend,
    switchChannel,
    cancel,
    reset
  };
}
